/* ============================================================
   CarePlix OneHealth — Fit-for-Work Readiness · multilingual strings
   English (default) · हिन्दी · বাংলা · தமிழ் · العربية (RTL) · Français
   t(lang, "a.b.c", { n: 60 }) → dotted key, English fallback, {var} interp.
   ============================================================ */

export const LANGS = [
  { code: "en", label: "EN", name: "English", dir: "ltr" },
  { code: "hi", label: "हि", name: "हिन्दी", dir: "ltr" },
  { code: "bn", label: "বা", name: "বাংলা", dir: "ltr" },
  { code: "ta", label: "த", name: "தமிழ்", dir: "ltr" },
  { code: "ar", label: "ع", name: "العربية", dir: "rtl" },
  { code: "fr", label: "FR", name: "Français", dir: "ltr" },
];

export const dirFor = (code) => (LANGS.find((l) => l.code === code)?.dir || "ltr");

export const STRINGS = {
  /* ───────────────────────── ENGLISH ───────────────────────── */
  en: {
    risk: { low: "Low", medium: "Medium", moderate: "Moderate", high: "High" },
    band: {
      excellent: "Excellent", good: "Good", average: "Average",
      fair: "Fair", poor: "Poor", low: "Low", normal: "Normal", high: "High",
    },
    common: { continue: "Continue", back: "Back", cancel: "Cancel", tryAgain: "Try Again", poweredByShort: "Powered by" },
    getstarted: {
      kicker: "Fit-for-Work · Contactless check",
      title1: "Start your shift", title2: "with a face scan.",
      sub: "Step up to the camera. In about {n} seconds we read your heart rate, oxygen, breathing and more — no wearables, no cuffs.",
      p1: "Contactless rPPG face scan",
      p2: "Measured against a safe range",
      p3: "One clear readiness verdict",
      cta: "Get Started",
      foot: "Not a medical diagnosis",
    },
    identify: {
      kicker: "Identify · Employee verification",
      matching: "Identifying you…",
      matchingSub: "Matching your face to the employee directory",
      verified: "Verified",
      badgeLabel: "Badge",
      startScan: "Start face scan",
      startHint: "Verified — start the contactless vitals scan when you're ready.",
      waitHint: "Hold still — verifying your identity",
      starting: "Starting scan…",
      notYou: "Not you? Enter details manually",
      notFoundKicker: "Couldn't verify employee",
      notFound: "Please enter your details manually to continue",
      enterManually: "Enter details manually",
      firstTime: "First time here? Register",
      registerButton: "Register",
      registerTitle: "Register worker",
      manualWhy: "You are not on record yet. These details create your worker record and are needed for the health scan.",
      dob: "Date of birth",
      computedAge: "Age",
      contact: "Contact number (optional)",
      alreadyExists: "That employee ID already exists at this site.",
      employeeId: "Employee ID",
      onlineTitle: "Check your worker record",
      onlineSub: "Enter your Employee ID to match it with the face photo you just took.",
      photoTitle: "Take your face photo",
      capturingPhoto: "Capturing…",
      onlineCheck: "Verify me",
      onlineChecking: "Checking…",
      onlineNotFound: "No worker was found with that Employee ID.",
      onlinePhotoMissing: "Your worker record has no profile photo. Please ask your site administrator for help.",
      onlineFaceRead: "Keep your face clearly visible and try again.",
      onlineFaceMismatch: "Your face did not match this worker record. Check the Employee ID and try again.",
      onlineError: "The online check could not be completed. Check your connection and try again.",
      siteKicker: "Site selection",
      siteTitle: "Select your site",
      siteSub: "Face check-in will match you against the workers of this site.",
      siteLoading: "Loading sites…",
      siteNone: "No sites are available yet.",
      siteError: "Couldn't load the site list — check the connection and try again.",
      sitePreparing: "Preparing site…",
      siteContinue: "Continue to face check-in",
      takePhoto: "Take photo",
      takeAgain: "Retake photo",
      photoHint: "Look at the camera and hold still — your photo will be taken automatically",
      registerStart: "Register & start scan",
      manualTitle: "Enter your details",
      manualHint: "Fill in your details, then start the scan.",
      fName: "Full name", fAge: "Age", fGender: "Gender", fHeight: "Height (cm)", fWeight: "Weight (kg)",
      ageOrDob: "Age or date of birth", dobEntry: "Date of birth", ageEntry: "Age",
      feet: "Feet", inches: "Inches", siteLabel: "Site", departmentLabel: "Department",
      fixedSiteLoading: "Loading site…", deptLoading: "Loading departments…",
      deptError: "Could not load this device's site and departments.", deptEmpty: "No departments are configured for this site.",
      retryDepartments: "Try again", deptSelect: "— Select department —",
      gMale: "Male", gFemale: "Female", gOther: "Other",
      screeningTitle: "Screenings for this worker",
      togglePulmo: "Pulmo (lung) screening",
      toggleCognitive: "Attention check (PVT-B)",
    },
    scan: {
      kicker: "FaceScan · Employee identification", seconds: "Seconds", bpm: "BPM",
      hr: "Heart rate", hrv: "HRV", sdnn: "HRV · SDNN", rmssd: "HRV · RMSSD", pnn50: "HRV · pNN50", spo2: "Blood oxygen", resp: "Breathing", stress: "Stress",
      bp: "Blood pressure", bpNormal: "Normal", bpElevated: "Elevated", bpHigh: "High",
      lvlLow: "Low", lvlNormal: "Normal", lvlElevated: "Elevated", lvlHigh: "High",
      opening: "Opening camera…", openingSub: "Line your face up inside the frame",
      prompt: "Keep still · Look into the camera", centerFace: "Center your face", noFace: "No face detected — scan paused",
      alignFace: "Align face", measuring: "Measuring", done: "Done", liveSignal: "Live rPPG signal",
      reading: "Reading your vitals…",
      errTitle: "Scan not completed", embedTitle: "Camera needs its own tab",
      embedMsg: "The camera can't run inside this embedded preview. Open the app in its own browser tab, then allow camera access when prompted.",
      openTab: "Open in its own tab",
      errCameraStart: "We couldn't start the camera scan. Please allow camera access, check your connection, and try again.",
      errStopped: "The camera scan stopped unexpectedly. Please allow camera access, check your connection, and try again.",
      errStartProper: "The scan couldn't start properly. Please try again.",
      errReadTime: "We couldn't read your vitals in time. Please try again.",
      errReadVitals: "We couldn't read your vitals. Please sit still in good light and try again.",
      errFinishTime: "The scan didn't finish in time. Please try again.",
      errLowFps: "The camera couldn't keep up during the scan. Close other apps, add more light, and try again.",
      errFroze: "The scan froze. Please try again.", errFeedStop: "The camera feed stopped. Please try again.",
      capturingHint: "Hold still — reading your vitals",
      chooseHint: "Scan complete — continue",
      doneHint: "Scan complete — continue",
      continue: "Continue",
    },
    pulmo: {
      kicker: "PulmoScreen · Lung check", title: "Lung screening",
      sub: "Record a firm cough and a sustained vowel “A” (/aː/). CarePlix analyses the sound for a lung screening score.",
      coughOnlySub: "Record 2–3 firm coughs. This journey collects the cough sample only and does not generate a Pulmo score.",
      needTab: "Microphone needs its own tab",
      needTabMsg: "The microphone can't run inside this embedded preview. Open the app in its own browser tab, then allow microphone access when prompted.",
      micDenied: "We couldn't access the microphone. Please allow mic access and try again.",
      step1Title: "Step 1 · Cough", step1Hint: "When recording starts, give 2–3 firm coughs into the mic.",
      step2Title: "Step 2 · Sustain vowel “A”", step2Hint: "Take a breath, then hold a steady vowel “A” (/aː/) until the timer ends.",
      startCough: "Start cough recording", startVowel: "Start vowel “A” recording",
      recording: "Recording…", recorded: "Recorded", rerecord: "Record again",
      uploading: "Uploading audio…",
      surveyTitle: "A few quick questions", surveySub: "Answer honestly — it sharpens the screening.",
      submit: "Submit answers", scoring: "Scoring…",
      scoreLabel: "Pulmo screening score", complete: "Lung screening complete",
      completeSub: "Pulmo assessment completed. Your results will appear in the final readiness report.",
      coughOnlyCompleteSub: "Your cough sample was collected. No Pulmo score was generated from this cough-only check.",
      errUpload: "We couldn't upload the recording. Please try again.",
      errSurvey: "We couldn't load the questions.", errScore: "We couldn't compute the score. Please try again.",
      errScoreAudio: "The score couldn't be generated from these recordings. Let's record the cough and vowel sounds again.",
    },
    pvtb: {
      kicker: "Attention · PVT-B", title: "Attention check",
      intro: "A quick reaction test. When the circle fills in, tap as fast as you can — but don't tap early. {n} rounds.",
      start: "Start", wait: "Wait…", tapNow: "TAP", tap: "Tap here",
      tooSoon: "Too soon — wait for the signal", round: "Round {a} of {b}", ms: "ms",
      complete: "Attention check complete", meanRt: "Average reaction", lapses: "Lapses",
      resultSharp: "Sharp and alert", resultOk: "Within range", resultSlow: "Slowed reactions — take care",
    },
    result: {
      badge: "Readiness", measured: "Measured vitals",
      pulmoScore: "Pulmo screening", attention: "Attention (PVT-B)",
      attentionMean: "Avg reaction", attentionLapses: "Lapses",
      uBpm: "bpm", uResp: "/min", uMs: "ms",
      scoresTitle: "Health scores", wellbeing: "Wellbeing", pulmonary: "Pulmonary health", alertness: "Alertness", na: "N/A",
      reportTitle: "Detailed report", colParam: "Parameter", colResult: "Result", colRange: "Reference range", colStatus: "Status",
      download: "Download report", preparing: "Preparing report…", reportHeading: "Fit-for-Work Readiness Report", worker: "Worker",
      bpExcellent: "Excellent", bpGood: "Good", bpAverage: "Average", bpFair: "Fair", bpPoor: "Poor",
      pMap: "Mean arterial pressure", pCardiacOut: "Cardiac output",
      pHba1c: "HbA1c (est.)", pDiabRisk: "Diabetes risk", pBmi: "BMI", pBmr: "Basal metabolic rate",
      pBloodVolume: "Blood volume", pBodyWater: "Body water", pBodyFat: "Body fat", pVo2max: "VO₂ max",
      hba1cOptimal: "Optimal", hba1cAbove: "Above optimal",
      hypertensionRisk: "Hypertension risk",
      cardiacRisk: "Cardiac risk",
      diabRiskNoteOptimal: "Diabetes risk: as your HbA1c is optimal (< 5.7), this value indicates the chance of developing diabetes in the near future.",
      diabRiskNoteAbove: "Diabetes risk: as your HbA1c is above optimal (≥ 5.7), this value indicates the likelihood of having a value above the optimal range.",
      attentionNote: "Average reaction shows how quickly you respond to a visual signal — faster means more alert. Lapses are unusually slow responses (≥ 400 ms), indicating momentary drops in attention, often linked to fatigue or drowsiness.",
      pFevFvc: "FEV1/FVC ratio", pOad: "Airway obstruction (OAD)", oadTrue: "Detected", oadFalse: "Not detected", unavailable: "Unavailable",
      coughSample: "Cough sample", collected: "Collected",
      statusNormal: "Normal", statusWatch: "Watch", statusAction: "Action",
      clearLabel: "CLEAR", cautionLabel: "CAUTION", referLabel: "REFER",
      clearTitle: "Fit for work today", clearBody: "Your vitals are within a safe range. You're good to start.",
      cautionTitle: "Fit with care", cautionBody: "One reading is slightly out of range with no red flags. Hydrate, ease in — fit to continue with monitoring.",
      referTitle: "See the health officer", referBody: "Your vitals need a check before safety-critical work. You're referred to the Occupational Health Centre — the clinician decides.",
      logged: "Logged to the digital Health Record", baselineUpdated: "Personal baseline updated",
      supervisor: "Supervisor notified", ohc: "Referred to the Occupational Health Centre",
      proceed: "Go to work", restart: "Run another worker",
      disclaimer: "This is a wellness screening to support your shift readiness — it is not a medical diagnosis. Vitals are measured contactlessly by CarePlix. If you feel unwell, please contact your health centre.",
    },
    siteJourney: {
      preparingKicker: "Device check", preparingTitle: "Preparing this station",
      preparingSub: "Loading the workers assigned to this site. Your details stay on this shared device.",
      preparingErrorTitle: "We could not prepare the station.", preparingErrorSub: "Check the connection and try again.",
      preparingRoster: "Securing the site roster",
      wellbeingKicker: "One more question", feelingQuestion: "Not feeling okay?",
      feelingPrompt: "Are you feeling okay right now?",
      notOkay: "No", notOkayHint: "I need support", imFine: "I'm fine",
      connectTitle: "Connect with the virtual OHC",
      connectSub: "Request a call from the Occupational Health Centre team. This records the request; it does not start a live call yet.",
      callTeam: "Request virtual OHC call", loggingRequest: "Logging request…",
      savingResult: "Saving your result…", thankYou: "Thank you. Saving your response…",
      requestLogged: "Your OHC request has been logged",
      requestLoggedSub: "The health team can follow up through the organisation dashboard.",
      resultSaved: "Your result has been saved", resultSavedSub: "You can now continue with your shift instructions.",
      dashboardSynced: "Result synced to the organisation dashboard",
      retrySave: "Try saving again", nextWorker: "Finish and start next worker",
    },
  },

  /* ───────────────────────── हिन्दी (Hindi) ───────────────────────── */
  hi: {
    risk: { low: "कम", medium: "मध्यम", moderate: "मध्यम", high: "उच्च" },
    common: { continue: "आगे बढ़ें", back: "वापस", tryAgain: "फिर से कोशिश करें", poweredByShort: "द्वारा संचालित" },
    getstarted: {
      kicker: "काम-योग्यता · संपर्क-रहित जांच",
      title1: "अपनी शिफ्ट शुरू करें", title2: "फेस स्कैन के साथ।",
      sub: "कैमरे के सामने आएं। लगभग {n} सेकंड में हम आपकी हृदय गति, ऑक्सीजन, श्वसन और बहुत कुछ पढ़ते हैं — कोई वियरेबल नहीं, कोई कफ नहीं।",
      p1: "संपर्क-रहित rPPG फेस स्कैन",
      p2: "सुरक्षित सीमा के मुकाबले मापा गया",
      p3: "एक स्पष्ट तैयारी निर्णय",
      cta: "शुरू करें",
      foot: "यह चिकित्सा निदान नहीं है",
    },
    identify: {
      kicker: "पहचान · कर्मचारी सत्यापन",
      matching: "आपकी पहचान हो रही है…",
      matchingSub: "आपके चेहरे का कर्मचारी निर्देशिका से मिलान",
      verified: "सत्यापित",
      badgeLabel: "बैज",
      startScan: "फेस स्कैन शुरू करें",
      startHint: "सत्यापित — तैयार होने पर संपर्क-रहित वाइटल्स स्कैन शुरू करें।",
      waitHint: "स्थिर रहें — आपकी पहचान सत्यापित हो रही है",
      starting: "स्कैन शुरू हो रहा है…",
      notYou: "आप नहीं? विवरण स्वयं भरें",
      notFoundKicker: "कर्मचारी सत्यापित नहीं हो सका",
      notFound: "जारी रखने के लिए कृपया अपना विवरण स्वयं भरें",
      enterManually: "विवरण स्वयं भरें",
      firstTime: "पहली बार आए हैं? पंजीकरण करें",
      registerButton: "पंजीकरण करें",
      registerTitle: "कर्मचारी पंजीकरण",
      manualWhy: "आपका रिकॉर्ड अभी नहीं है। ये विवरण आपका कर्मचारी रिकॉर्ड बनाते हैं और स्वास्थ्य स्कैन के लिए आवश्यक हैं।",
      dob: "जन्म तिथि",
      computedAge: "आयु",
      contact: "संपर्क नंबर (वैकल्पिक)",
      alreadyExists: "यह कर्मचारी आईडी इस साइट पर पहले से मौजूद है।",
      employeeId: "कर्मचारी आईडी",
      onlineTitle: "अपना कर्मचारी रिकॉर्ड जाँचें",
      onlineSub: "अभी ली गई चेहरे की फोटो से मिलान करने के लिए अपनी कर्मचारी आईडी दर्ज करें।",
      photoTitle: "अपने चेहरे की फोटो लें",
      capturingPhoto: "फोटो ली जा रही है…",
      onlineCheck: "मुझे सत्यापित करें",
      onlineChecking: "जाँच हो रही है…",
      onlineNotFound: "इस कर्मचारी आईडी से कोई रिकॉर्ड नहीं मिला।",
      onlinePhotoMissing: "आपके कर्मचारी रिकॉर्ड में प्रोफ़ाइल फ़ोटो नहीं है। कृपया साइट प्रशासक से सहायता लें।",
      onlineFaceRead: "अपना चेहरा साफ़ दिखाएँ और फिर प्रयास करें।",
      onlineFaceMismatch: "आपका चेहरा इस कर्मचारी रिकॉर्ड से मेल नहीं खाया। आईडी जाँचकर फिर प्रयास करें।",
      onlineError: "ऑनलाइन जाँच पूरी नहीं हो सकी। कनेक्शन जाँचकर फिर प्रयास करें।",
      siteKicker: "साइट चयन",
      siteTitle: "अपनी साइट चुनें",
      siteSub: "फेस चेक-इन आपको इस साइट के कर्मचारियों से मिलाएगा।",
      siteLoading: "साइटें लोड हो रही हैं…",
      siteNone: "अभी कोई साइट उपलब्ध नहीं है।",
      siteError: "साइट सूची लोड नहीं हो सकी — कनेक्शन जाँचें और पुनः प्रयास करें।",
      sitePreparing: "साइट तैयार हो रही है…",
      siteContinue: "फेस चेक-इन पर जाएँ",
      takePhoto: "फोटो लें",
      takeAgain: "फिर से फोटो लें",
      photoHint: "कैमरे की ओर देखें और स्थिर रहें — आपकी फोटो अपने आप ली जाएगी",
      registerStart: "पंजीकरण करें और स्कैन शुरू करें",
      manualTitle: "अपना विवरण भरें",
      manualHint: "अपना विवरण भरें, फिर स्कैन शुरू करें।",
      fName: "पूरा नाम", fAge: "आयु", fGender: "लिंग", fHeight: "ऊँचाई (सेमी)", fWeight: "वज़न (किग्रा)",
      ageOrDob: "आयु या जन्म तिथि", dobEntry: "जन्म तिथि", ageEntry: "आयु",
      feet: "फुट", inches: "इंच", siteLabel: "साइट", departmentLabel: "विभाग",
      fixedSiteLoading: "साइट लोड हो रही है…", deptLoading: "विभाग लोड हो रहे हैं…",
      deptError: "इस डिवाइस की साइट और विभाग लोड नहीं हो सके।", deptEmpty: "इस साइट के लिए कोई विभाग कॉन्फ़िगर नहीं है।",
      retryDepartments: "फिर कोशिश करें", deptSelect: "— विभाग चुनें —",
      gMale: "पुरुष", gFemale: "महिला", gOther: "अन्य",
      screeningTitle: "इस कर्मचारी हेतु स्क्रीनिंग",
      togglePulmo: "पल्मो (फेफड़ा) स्क्रीनिंग",
      toggleCognitive: "ध्यान जांच (PVT-B)",
    },
    scan: {
      kicker: "फेसस्कैन · कर्मचारी पहचान", seconds: "सेकंड", bpm: "BPM",
      hr: "हृदय गति", hrv: "HRV", sdnn: "HRV · SDNN", rmssd: "HRV · RMSSD", pnn50: "HRV · pNN50", spo2: "रक्त ऑक्सीजन", resp: "श्वसन", stress: "तनाव",
      bp: "रक्तचाप", bpNormal: "सामान्य", bpElevated: "बढ़ा हुआ", bpHigh: "उच्च",
      lvlLow: "कम", lvlNormal: "सामान्य", lvlElevated: "बढ़ा हुआ", lvlHigh: "उच्च",
      opening: "कैमरा खुल रहा है…", openingSub: "अपना चेहरा फ़्रेम के अंदर रखें",
      prompt: "स्थिर रहें · कैमरे में देखें", centerFace: "अपना चेहरा केंद्र में रखें", noFace: "कोई चेहरा नहीं मिला — स्कैन रुका",
      alignFace: "चेहरा सीधा करें", measuring: "माप रहे हैं", done: "पूर्ण", liveSignal: "लाइव rPPG सिग्नल",
      reading: "आपके वाइटल्स पढ़े जा रहे हैं…",
      errTitle: "स्कैन पूरा नहीं हुआ", embedTitle: "कैमरे को अपने टैब की ज़रूरत है",
      embedMsg: "यह कैमरा इस एम्बेडेड प्रीव्यू में नहीं चल सकता। ऐप को अपने ब्राउज़र टैब में खोलें, फिर पूछे जाने पर कैमरा अनुमति दें।",
      openTab: "अपने टैब में खोलें",
      errCameraStart: "हम कैमरा स्कैन शुरू नहीं कर सके। कृपया कैमरा अनुमति दें, कनेक्शन जांचें और फिर कोशिश करें।",
      errStopped: "कैमरा स्कैन अचानक रुक गया। कृपया कैमरा अनुमति दें, कनेक्शन जांचें और फिर कोशिश करें।",
      errStartProper: "स्कैन ठीक से शुरू नहीं हो सका। कृपया फिर कोशिश करें।",
      errReadTime: "हम समय पर आपके वाइटल्स नहीं पढ़ सके। कृपया फिर कोशिश करें।",
      errReadVitals: "हम आपके वाइटल्स नहीं पढ़ सके। कृपया अच्छी रोशनी में स्थिर बैठें और फिर कोशिश करें।",
      errFinishTime: "स्कैन समय पर पूरा नहीं हुआ। कृपया फिर कोशिश करें।",
      errLowFps: "स्कैन के दौरान कैमरा धीमा पड़ गया। अन्य ऐप बंद करें, रोशनी बढ़ाएँ और फिर कोशिश करें।",
      errFroze: "स्कैन रुक गया। कृपया फिर कोशिश करें।", errFeedStop: "कैमरा फ़ीड रुक गई। कृपया फिर कोशिश करें।",
      capturingHint: "स्थिर रहें — आपके वाइटल्स पढ़े जा रहे हैं",
      chooseHint: "स्कैन पूर्ण — आगे बढ़ें",
      doneHint: "स्कैन पूर्ण — आगे बढ़ें",
      continue: "आगे बढ़ें",
    },
    pulmo: {
      kicker: "PulmoScreen · फेफड़ा जांच", title: "फेफड़ा स्क्रीनिंग",
      sub: "एक ज़ोरदार खाँसी और एक लंबा स्वर “आ” (/aː/) रिकॉर्ड करें। CarePlix ध्वनि का विश्लेषण कर फेफड़ा स्क्रीनिंग स्कोर देता है।",
      coughOnlySub: "2–3 बार ज़ोर से खाँसकर रिकॉर्ड करें। इस यात्रा में केवल खाँसी का नमूना लिया जाता है; पल्मो स्कोर नहीं बनाया जाता।",
      needTab: "माइक्रोफ़ोन को अपने टैब की ज़रूरत है",
      needTabMsg: "माइक्रोफ़ोन इस एम्बेडेड प्रीव्यू में नहीं चल सकता। ऐप को अपने ब्राउज़र टैब में खोलें, फिर पूछे जाने पर माइक अनुमति दें।",
      micDenied: "हम माइक्रोफ़ोन तक नहीं पहुँच सके। कृपया माइक अनुमति दें और फिर कोशिश करें।",
      step1Title: "चरण 1 · खाँसी", step1Hint: "रिकॉर्डिंग शुरू होते ही माइक में 2–3 ज़ोरदार खाँसी लें।",
      step2Title: "चरण 2 · स्वर “आ” रखें", step2Hint: "साँस लें, फिर टाइमर समाप्त होने तक स्थिर स्वर “आ” (/aː/) बनाए रखें।",
      startCough: "खाँसी रिकॉर्डिंग शुरू करें", startVowel: "स्वर “आ” रिकॉर्डिंग शुरू करें",
      recording: "रिकॉर्ड हो रहा है…", recorded: "रिकॉर्ड हुआ", rerecord: "फिर रिकॉर्ड करें",
      uploading: "ऑडियो अपलोड हो रहा है…",
      surveyTitle: "कुछ त्वरित प्रश्न", surveySub: "ईमानदारी से उत्तर दें — इससे स्क्रीनिंग बेहतर होती है।",
      submit: "उत्तर जमा करें", scoring: "स्कोर निकाला जा रहा है…",
      scoreLabel: "पल्मो स्क्रीनिंग स्कोर", complete: "फेफड़ा स्क्रीनिंग पूर्ण",
      completeSub: "पल्मो मूल्यांकन पूरा हुआ। आपके परिणाम अंतिम तैयारी रिपोर्ट में दिखाई देंगे।",
      coughOnlyCompleteSub: "आपकी खाँसी का नमूना दर्ज हो गया। इस केवल-खाँसी जाँच से पल्मो स्कोर नहीं बनाया गया।",
      errUpload: "हम रिकॉर्डिंग अपलोड नहीं कर सके। कृपया फिर कोशिश करें।",
      errSurvey: "हम प्रश्न लोड नहीं कर सके।", errScore: "हम स्कोर नहीं निकाल सके। कृपया फिर कोशिश करें।",
      errScoreAudio: "इन रिकॉर्डिंग से स्कोर नहीं बन सका। खांसी और स्वर की रिकॉर्डिंग फिर से करें।",
    },
    pvtb: {
      kicker: "ध्यान · PVT-B", title: "ध्यान जांच",
      intro: "एक त्वरित प्रतिक्रिया परीक्षण। जब घेरा भर जाए, जितनी तेज़ हो सके टैप करें — पर जल्दी टैप न करें। {n} राउंड।",
      start: "शुरू करें", wait: "रुकें…", tapNow: "टैप", tap: "यहाँ टैप करें",
      tooSoon: "बहुत जल्दी — संकेत की प्रतीक्षा करें", round: "राउंड {a}/{b}", ms: "मि.से.",
      complete: "ध्यान जांच पूर्ण", meanRt: "औसत प्रतिक्रिया", lapses: "चूक",
      resultSharp: "सतर्क व तेज़", resultOk: "सीमा के भीतर", resultSlow: "प्रतिक्रिया धीमी — सावधानी रखें",
    },
    result: {
      badge: "तैयारी", measured: "मापे गए वाइटल्स",
      pulmoScore: "पल्मो स्क्रीनिंग", attention: "ध्यान (PVT-B)",
      attentionMean: "औसत प्रतिक्रिया", attentionLapses: "चूक",
      uBpm: "बीपीएम", uResp: "/मिनट", uMs: "मि.से.",
      scoresTitle: "स्वास्थ्य स्कोर", wellbeing: "सेहत", pulmonary: "फेफड़ा स्वास्थ्य", alertness: "सतर्कता", na: "उपलब्ध नहीं",
      reportTitle: "विस्तृत रिपोर्ट", colParam: "पैरामीटर", colResult: "परिणाम", colRange: "संदर्भ सीमा", colStatus: "स्थिति",
      download: "रिपोर्ट डाउनलोड करें", preparing: "रिपोर्ट तैयार हो रही है…", reportHeading: "काम-योग्यता तैयारी रिपोर्ट", worker: "कर्मचारी",
      bpExcellent: "उत्कृष्ट", bpGood: "अच्छा", bpAverage: "औसत", bpFair: "साधारण", bpPoor: "खराब",
      pMap: "औसत धमनी दाब", pCardiacOut: "कार्डियक आउटपुट",
      pHba1c: "HbA1c (अनुमानित)", pDiabRisk: "मधुमेह जोखिम", pBmi: "बीएमआई", pBmr: "आधारभूत चयापचय दर",
      pBloodVolume: "रक्त आयतन", pBodyWater: "शरीर में जल", pBodyFat: "शरीर की वसा", pVo2max: "VO₂ मैक्स",
      hba1cOptimal: "इष्टतम", hba1cAbove: "इष्टतम से अधिक",
      hypertensionRisk: "उच्च रक्तचाप जोखिम",
      cardiacRisk: "हृदय जोखिम",
      diabRiskNoteOptimal: "मधुमेह जोखिम: चूँकि आपका HbA1c इष्टतम (< 5.7) है, यह मान निकट भविष्य में मधुमेह होने की संभावना दर्शाता है।",
      diabRiskNoteAbove: "मधुमेह जोखिम: चूँकि आपका HbA1c इष्टतम से अधिक (≥ 5.7) है, यह मान इष्टतम सीमा से ऊपर मान होने की संभावना दर्शाता है।",
      attentionNote: "औसत प्रतिक्रिया दिखाती है कि आप दृश्य संकेत पर कितनी तेज़ी से प्रतिक्रिया देते हैं — जितनी तेज़, उतनी अधिक सतर्कता। चूक असामान्य रूप से धीमी प्रतिक्रियाएँ (≥ 400 मि.से.) हैं, जो ध्यान में क्षणिक गिरावट दर्शाती हैं — अक्सर थकान या उनींदापन से जुड़ी।",
      pFevFvc: "FEV1/FVC अनुपात", pOad: "वायुमार्ग अवरोध (OAD)", oadTrue: "पाया गया", oadFalse: "नहीं पाया गया", unavailable: "उपलब्ध नहीं",
      coughSample: "खाँसी का नमूना", collected: "दर्ज हुआ",
      statusNormal: "सामान्य", statusWatch: "ध्यान दें", statusAction: "कार्रवाई",
      clearLabel: "उपयुक्त", cautionLabel: "सावधानी", referLabel: "रेफ़र",
      clearTitle: "आज काम के लिए उपयुक्त", clearBody: "आपके वाइटल्स सुरक्षित सीमा में हैं। आप शुरू कर सकते हैं।",
      cautionTitle: "सावधानी के साथ उपयुक्त", cautionBody: "एक रीडिंग थोड़ी सीमा से बाहर है, कोई चेतावनी संकेत नहीं। पानी पिएं, धीरे शुरू करें — निगरानी में जारी रखने योग्य।",
      referTitle: "स्वास्थ्य अधिकारी से मिलें", referBody: "सुरक्षा-महत्वपूर्ण कार्य से पहले आपके वाइटल्स की जांच ज़रूरी है। आपको व्यावसायिक स्वास्थ्य केंद्र भेजा गया है — निर्णय चिकित्सक करता है।",
      logged: "डिजिटल स्वास्थ्य रिकॉर्ड में दर्ज", baselineUpdated: "व्यक्तिगत बेसलाइन अपडेट",
      supervisor: "पर्यवेक्षक को सूचित किया", ohc: "व्यावसायिक स्वास्थ्य केंद्र भेजा गया",
      proceed: "काम पर जाएं", restart: "अगला कर्मचारी शुरू करें",
      disclaimer: "यह आपकी शिफ्ट-तैयारी में सहायता के लिए एक वेलनेस जांच है — यह चिकित्सा निदान नहीं है। वाइटल्स CarePlix द्वारा संपर्क-रहित मापे जाते हैं। अस्वस्थ महसूस होने पर कृपया स्वास्थ्य केंद्र से संपर्क करें।",
    },
    siteJourney: {
      preparingKicker: "डिवाइस जाँच", preparingTitle: "यह स्टेशन तैयार हो रहा है",
      preparingSub: "इस साइट के कर्मचारियों की सूची लोड हो रही है। आपका विवरण इसी साझा डिवाइस पर रहता है।",
      preparingErrorTitle: "स्टेशन तैयार नहीं हो सका।", preparingErrorSub: "कनेक्शन जाँचें और फिर कोशिश करें।",
      preparingRoster: "साइट रोस्टर सुरक्षित रूप से लोड हो रहा है",
      wellbeingKicker: "एक आख़िरी सवाल", feelingQuestion: "ठीक महसूस नहीं कर रहे?",
      feelingPrompt: "क्या आप अभी ठीक महसूस कर रहे हैं?",
      notOkay: "नहीं", notOkayHint: "मुझे सहायता चाहिए", imFine: "मैं ठीक हूँ",
      connectTitle: "वर्चुअल OHC से जुड़ें",
      connectSub: "व्यावसायिक स्वास्थ्य केंद्र की टीम से कॉल का अनुरोध करें। अभी केवल अनुरोध दर्ज होगा; लाइव कॉल शुरू नहीं होगी।",
      callTeam: "वर्चुअल OHC कॉल का अनुरोध करें", loggingRequest: "अनुरोध दर्ज हो रहा है…",
      savingResult: "परिणाम सहेजा जा रहा है…", thankYou: "धन्यवाद। आपका जवाब सहेजा जा रहा है…",
      requestLogged: "आपका OHC अनुरोध दर्ज हो गया",
      requestLoggedSub: "स्वास्थ्य टीम संगठन डैशबोर्ड से आपसे संपर्क कर सकती है।",
      resultSaved: "आपका परिणाम सहेज लिया गया", resultSavedSub: "अब आप शिफ्ट के निर्देशों के अनुसार आगे बढ़ सकते हैं।",
      dashboardSynced: "परिणाम संगठन डैशबोर्ड में सिंक हो गया",
      retrySave: "फिर से सहेजें", nextWorker: "समाप्त करें और अगला कर्मचारी शुरू करें",
    },
  },

  /* ───────────────────────── বাংলা (Bengali) ───────────────────────── */
  bn: {
    risk: { low: "কম", medium: "মাঝারি", moderate: "মাঝারি", high: "উচ্চ" },
    band: { excellent: "চমৎকার", good: "ভালো", average: "গড়", fair: "মোটামুটি", poor: "খারাপ", low: "কম", normal: "স্বাভাবিক", high: "উচ্চ" },
    common: { continue: "এগিয়ে যান", back: "ফিরে যান", cancel: "বাতিল", tryAgain: "আবার চেষ্টা করুন", poweredByShort: "পরিচালনায়" },
    identify: {
      kicker: "পরিচয় · কর্মী যাচাই", matching: "আপনাকে শনাক্ত করা হচ্ছে…",
      matchingSub: "কর্মী তালিকার সঙ্গে আপনার মুখ মেলানো হচ্ছে", verified: "যাচাইকৃত", badgeLabel: "ব্যাজ",
      startScan: "ফেস স্ক্যান শুরু করুন", startHint: "যাচাই সম্পন্ন — প্রস্তুত হলে স্পর্শহীন ভাইটাল স্ক্যান শুরু করুন।",
      waitHint: "স্থির থাকুন — আপনার পরিচয় যাচাই হচ্ছে", starting: "স্ক্যান শুরু হচ্ছে…",
      notFoundKicker: "কর্মীকে যাচাই করা যায়নি", notFound: "চালিয়ে যেতে আপনার তথ্য লিখুন",
      enterManually: "তথ্য লিখুন", firstTime: "প্রথমবার? নিবন্ধন করুন", registerButton: "নিবন্ধন করুন", registerTitle: "কর্মী নিবন্ধন",
      manualWhy: "আপনার রেকর্ড এখনও নেই। এই তথ্য দিয়ে কর্মী রেকর্ড তৈরি হবে এবং স্বাস্থ্য স্ক্যান সম্পন্ন হবে।",
      dob: "জন্মতারিখ", computedAge: "বয়স", contact: "যোগাযোগ নম্বর (ঐচ্ছিক)",
      alreadyExists: "এই কর্মী আইডি এই সাইটে আগে থেকেই আছে।",
      employeeId: "কর্মী আইডি", onlineTitle: "আপনার কর্মী রেকর্ড যাচাই করুন",
      onlineSub: "এইমাত্র তোলা মুখের ছবির সাথে মেলাতে আপনার কর্মী আইডি লিখুন।",
      photoTitle: "আপনার মুখের ছবি তুলুন", capturingPhoto: "ছবি তোলা হচ্ছে…",
      onlineCheck: "আমাকে যাচাই করুন", onlineChecking: "যাচাই হচ্ছে…",
      onlineNotFound: "এই কর্মী আইডিতে কোনো রেকর্ড পাওয়া যায়নি।",
      onlinePhotoMissing: "আপনার কর্মী রেকর্ডে প্রোফাইল ছবি নেই। সাইট প্রশাসকের সহায়তা নিন।",
      onlineFaceRead: "মুখটি স্পষ্টভাবে দেখিয়ে আবার চেষ্টা করুন।",
      onlineFaceMismatch: "আপনার মুখ এই কর্মী রেকর্ডের সঙ্গে মেলেনি। আইডি দেখে আবার চেষ্টা করুন।",
      onlineError: "অনলাইন যাচাই সম্পন্ন হয়নি। সংযোগ দেখে আবার চেষ্টা করুন।",
      takePhoto: "ছবি তুলুন", takeAgain: "আবার ছবি তুলুন", photoHint: "ক্যামেরার দিকে তাকিয়ে স্থির থাকুন — ছবি স্বয়ংক্রিয়ভাবে তোলা হবে",
      registerStart: "নিবন্ধন করে স্ক্যান শুরু করুন", manualTitle: "আপনার তথ্য দিন",
      manualHint: "তথ্য পূরণ করে স্ক্যান শুরু করুন।",
      fName: "পূর্ণ নাম", fAge: "বয়স", fGender: "লিঙ্গ", fHeight: "উচ্চতা (সেমি)", fWeight: "ওজন (কেজি)",
      ageOrDob: "বয়স বা জন্মতারিখ", dobEntry: "জন্মতারিখ", ageEntry: "বয়স",
      feet: "ফুট", inches: "ইঞ্চি", siteLabel: "সাইট", departmentLabel: "বিভাগ",
      fixedSiteLoading: "সাইট লোড হচ্ছে…", deptLoading: "বিভাগ লোড হচ্ছে…",
      deptError: "এই ডিভাইসের সাইট ও বিভাগ লোড করা যায়নি।", deptEmpty: "এই সাইটে কোনো বিভাগ কনফিগার করা নেই।",
      retryDepartments: "আবার চেষ্টা করুন", deptSelect: "— বিভাগ নির্বাচন করুন —",
      gMale: "পুরুষ", gFemale: "মহিলা", gOther: "অন্যান্য",
      screeningTitle: "এই কর্মীর পরীক্ষা", togglePulmo: "ফুসফুস পরীক্ষা", toggleCognitive: "মনোযোগ পরীক্ষা",
    },
    scan: {
      kicker: "ফেসস্ক্যান · কর্মী পরিচয়", seconds: "সেকেন্ড",
      hr: "হৃদস্পন্দন", hrv: "HRV", sdnn: "HRV · SDNN", rmssd: "HRV · RMSSD", pnn50: "HRV · pNN50",
      spo2: "রক্তে অক্সিজেন", resp: "শ্বাস-প্রশ্বাস", stress: "চাপ", bp: "রক্তচাপ",
      opening: "ক্যামেরা খোলা হচ্ছে…", openingSub: "ফ্রেমের মধ্যে মুখ রাখুন",
      prompt: "স্থির থাকুন · ক্যামেরার দিকে তাকান", centerFace: "মুখটি মাঝখানে রাখুন",
      noFace: "মুখ পাওয়া যায়নি — স্ক্যান থেমেছে", alignFace: "মুখ ঠিক করুন",
      measuring: "মাপা হচ্ছে", done: "সম্পন্ন", liveSignal: "লাইভ rPPG সংকেত",
      errTitle: "স্ক্যান সম্পন্ন হয়নি", embedTitle: "ক্যামেরার জন্য আলাদা ট্যাব দরকার",
      embedMsg: "এম্বেডেড প্রিভিউতে ক্যামেরা চলবে না। অ্যাপটি আলাদা ট্যাবে খুলে ক্যামেরার অনুমতি দিন।",
      openTab: "আলাদা ট্যাবে খুলুন",
      errCameraStart: "ক্যামেরা স্ক্যান শুরু করা যায়নি। অনুমতি ও সংযোগ দেখে আবার চেষ্টা করুন।",
      errStopped: "ক্যামেরা স্ক্যান হঠাৎ থেমে গেছে। আবার চেষ্টা করুন।",
      errStartProper: "স্ক্যান ঠিকভাবে শুরু হয়নি। আবার চেষ্টা করুন।",
      errReadTime: "সময়ের মধ্যে ভাইটাল পড়া যায়নি। আবার চেষ্টা করুন।",
      errReadVitals: "ভাইটাল পড়া যায়নি। ভালো আলোতে স্থির থেকে আবার চেষ্টা করুন।",
      errFinishTime: "সময়ের মধ্যে স্ক্যান শেষ হয়নি। আবার চেষ্টা করুন।",
      errLowFps: "স্ক্যানের সময় ক্যামেরা ধীর ছিল। অন্য অ্যাপ বন্ধ করে আলো বাড়িয়ে আবার চেষ্টা করুন।",
      errFroze: "স্ক্যান আটকে গেছে। আবার চেষ্টা করুন।", errFeedStop: "ক্যামেরা ফিড বন্ধ হয়েছে। আবার চেষ্টা করুন।",
      capturingHint: "স্থির থাকুন — আপনার ভাইটাল পড়া হচ্ছে", chooseHint: "স্ক্যান সম্পন্ন — এগিয়ে যান",
      doneHint: "স্ক্যান সম্পন্ন — এগিয়ে যান", continue: "এগিয়ে যান",
    },
    pulmo: {
      kicker: "পালমোস্ক্রিন · ফুসফুস পরীক্ষা", title: "ফুসফুস পরীক্ষা",
      coughOnlySub: "মাইক্রোফোনে ২–৩ বার জোরে কাশি দিন। শুধু কাশির নমুনা নেওয়া হবে; পালমো স্কোর তৈরি হবে না।",
      needTab: "মাইক্রোফোনের জন্য আলাদা ট্যাব দরকার",
      needTabMsg: "এম্বেডেড প্রিভিউতে মাইক্রোফোন চলবে না। অ্যাপটি আলাদা ট্যাবে খুলে অনুমতি দিন।",
      micDenied: "মাইক্রোফোন ব্যবহার করা যায়নি। অনুমতি দিয়ে আবার চেষ্টা করুন।",
      step1Title: "কাশির নমুনা", step1Hint: "রেকর্ডিং শুরু হলে মাইক্রোফোনে ২–৩ বার জোরে কাশি দিন।",
      startCough: "কাশি রেকর্ড করুন", recording: "রেকর্ড হচ্ছে…", uploading: "অডিও পাঠানো হচ্ছে…",
      complete: "ফুসফুস পরীক্ষা সম্পন্ন",
      coughOnlyCompleteSub: "আপনার কাশির নমুনা নেওয়া হয়েছে। শুধু কাশির এই পরীক্ষায় পালমো স্কোর তৈরি হয়নি।",
      errUpload: "রেকর্ডিং পাঠানো যায়নি। আবার চেষ্টা করুন।",
    },
    pvtb: {
      kicker: "মনোযোগ · PVT-B", title: "মনোযোগ পরীক্ষা",
      intro: "বৃত্ত ভরে উঠলে যত দ্রুত পারেন ট্যাপ করুন—আগে ট্যাপ করবেন না। মোট {n} রাউন্ড।",
      start: "শুরু করুন", wait: "অপেক্ষা করুন…", tapNow: "ট্যাপ", tap: "এখানে ট্যাপ করুন",
      tooSoon: "খুব তাড়াতাড়ি — সংকেতের জন্য অপেক্ষা করুন", round: "রাউন্ড {a} / {b}", ms: "মি.সে.",
      complete: "মনোযোগ পরীক্ষা সম্পন্ন", meanRt: "গড় প্রতিক্রিয়া", lapses: "বিলম্ব",
      resultSharp: "সতর্ক ও দ্রুত", resultOk: "স্বাভাবিক সীমায়", resultSlow: "প্রতিক্রিয়া ধীর — সাবধান থাকুন",
    },
    result: {
      badge: "কাজের প্রস্তুতি", pulmoScore: "ফুসফুস পরীক্ষা", attention: "মনোযোগ",
      attentionMean: "গড় প্রতিক্রিয়া", attentionLapses: "বিলম্ব", uMs: "মি.সে.",
      scoresTitle: "স্বাস্থ্য স্কোর", wellbeing: "সুস্থতা", pulmonary: "ফুসফুসের স্বাস্থ্য", alertness: "সতর্কতা", na: "প্রযোজ্য নয়",
      reportTitle: "বিস্তারিত রিপোর্ট", colParam: "পরিমাপ", colResult: "ফল", colRange: "স্বাভাবিক সীমা", colStatus: "অবস্থা",
      download: "রিপোর্ট ডাউনলোড করুন", preparing: "রিপোর্ট তৈরি হচ্ছে…",
      pFevFvc: "FEV1/FVC অনুপাত", pOad: "শ্বাসনালী বাধা", oadTrue: "পাওয়া গেছে", oadFalse: "পাওয়া যায়নি", unavailable: "উপলভ্য নয়",
      coughSample: "কাশির নমুনা", collected: "সংগ্রহ হয়েছে",
      clearLabel: "যোগ্য", cautionLabel: "সতর্কতা", referLabel: "রেফার",
      clearTitle: "আজ কাজের জন্য উপযুক্ত", clearBody: "আপনার ভাইটাল নিরাপদ সীমায় আছে। কাজ শুরু করতে পারেন।",
      cautionTitle: "সতর্কতার সঙ্গে উপযুক্ত", cautionBody: "একটি রিডিং সামান্য সীমার বাইরে। পানি পান করে ধীরে শুরু করুন।",
      referTitle: "স্বাস্থ্য কর্মকর্তার সঙ্গে দেখা করুন", referBody: "নিরাপত্তা-সংবেদনশীল কাজের আগে আপনার ভাইটাল পরীক্ষা করা দরকার।",
      logged: "ডিজিটাল স্বাস্থ্য রেকর্ডে নথিভুক্ত", baselineUpdated: "ব্যক্তিগত বেসলাইন আপডেট হয়েছে",
      supervisor: "সুপারভাইজারকে জানানো হয়েছে", ohc: "পেশাগত স্বাস্থ্য কেন্দ্রে রেফার করা হয়েছে",
      proceed: "কাজে যান", restart: "পরবর্তী কর্মী", disclaimer: "এটি শিফট প্রস্তুতির সহায়ক সুস্থতা পরীক্ষা, চিকিৎসা নির্ণয় নয়। অসুস্থ লাগলে স্বাস্থ্য কেন্দ্রে যোগাযোগ করুন।",
    },
    siteJourney: {
      preparingKicker: "ডিভাইস পরীক্ষা", preparingTitle: "স্টেশন প্রস্তুত হচ্ছে",
      preparingSub: "এই সাইটের কর্মী তালিকা লোড হচ্ছে। আপনার তথ্য এই শেয়ার করা ডিভাইসেই থাকে।",
      preparingErrorTitle: "স্টেশন প্রস্তুত করা যায়নি।", preparingErrorSub: "সংযোগ দেখে আবার চেষ্টা করুন।",
      preparingRoster: "সাইটের কর্মী তালিকা সুরক্ষিতভাবে লোড হচ্ছে",
      wellbeingKicker: "আর একটি প্রশ্ন", feelingQuestion: "শরীর ভালো লাগছে না?",
      feelingPrompt: "আপনি কি এখন ভালো বোধ করছেন?",
      notOkay: "না", notOkayHint: "আমার সহায়তা দরকার", imFine: "আমি ভালো আছি", connectTitle: "ভার্চুয়াল OHC-এর সঙ্গে যুক্ত হন",
      connectSub: "পেশাগত স্বাস্থ্য কেন্দ্রের দলের কাছ থেকে কলের অনুরোধ করুন। এখন শুধু অনুরোধটি নথিভুক্ত হবে; লাইভ কল শুরু হবে না।",
      callTeam: "ভার্চুয়াল OHC কলের অনুরোধ করুন", loggingRequest: "অনুরোধ নথিভুক্ত হচ্ছে…",
      savingResult: "ফল সংরক্ষণ হচ্ছে…", thankYou: "ধন্যবাদ। আপনার উত্তর সংরক্ষণ হচ্ছে…",
      requestLogged: "আপনার OHC অনুরোধ নথিভুক্ত হয়েছে", requestLoggedSub: "স্বাস্থ্য দল প্রতিষ্ঠানের ড্যাশবোর্ড থেকে যোগাযোগ করতে পারবে।",
      resultSaved: "আপনার ফল সংরক্ষিত হয়েছে", resultSavedSub: "এখন শিফটের নির্দেশনা অনুযায়ী এগিয়ে যান।",
      dashboardSynced: "ফল প্রতিষ্ঠানের ড্যাশবোর্ডে সিঙ্ক হয়েছে",
      retrySave: "আবার সংরক্ষণ করুন", nextWorker: "শেষ করে পরবর্তী কর্মী শুরু করুন",
    },
  },

  /* ───────────────────────── தமிழ் (Tamil) ───────────────────────── */
  ta: {
    risk: { low: "குறைவு", medium: "மிதமான", moderate: "மிதமான", high: "அதிகம்" },
    band: { excellent: "மிகச் சிறப்பு", good: "நன்று", average: "சராசரி", fair: "பரவாயில்லை", poor: "மோசம்", low: "குறைவு", normal: "இயல்பு", high: "அதிகம்" },
    common: { continue: "தொடரவும்", back: "பின்செல்", cancel: "ரத்து", tryAgain: "மீண்டும் முயற்சிக்கவும்", poweredByShort: "வழங்குவது" },
    identify: {
      kicker: "அடையாளம் · பணியாளர் சரிபார்ப்பு", matching: "உங்களை அடையாளம் காண்கிறோம்…",
      matchingSub: "பணியாளர் பட்டியலுடன் உங்கள் முகம் பொருத்தப்படுகிறது", verified: "சரிபார்க்கப்பட்டது", badgeLabel: "அட்டை",
      startScan: "முக ஸ்கேனைத் தொடங்கவும்", startHint: "சரிபார்ப்பு முடிந்தது — தயாரானதும் உயிரளவு ஸ்கேனைத் தொடங்கவும்.",
      waitHint: "அசையாமல் இருங்கள் — அடையாளம் சரிபார்க்கப்படுகிறது", starting: "ஸ்கேன் தொடங்குகிறது…",
      notFoundKicker: "பணியாளரைச் சரிபார்க்க முடியவில்லை", notFound: "தொடர உங்கள் விவரங்களை உள்ளிடவும்",
      enterManually: "விவரங்களை உள்ளிடவும்", firstTime: "முதல் முறையா? பதிவு செய்யவும்", registerButton: "பதிவு செய்யவும்", registerTitle: "பணியாளர் பதிவு",
      manualWhy: "உங்கள் பதிவு இன்னும் இல்லை. இந்த விவரங்கள் பணியாளர் பதிவை உருவாக்கவும் உடல்நல ஸ்கேனை நடத்தவும் பயன்படும்.",
      dob: "பிறந்த தேதி", computedAge: "வயது", contact: "தொடர்பு எண் (விருப்பம்)",
      alreadyExists: "இந்த பணியாளர் எண் இத்தளத்தில் ஏற்கனவே உள்ளது.",
      employeeId: "பணியாளர் எண்", onlineTitle: "உங்கள் பணியாளர் பதிவைச் சரிபார்க்கவும்",
      onlineSub: "இப்போது எடுத்த முகப் படத்துடன் பொருத்த உங்கள் பணியாளர் எண்ணை உள்ளிடவும்.",
      photoTitle: "உங்கள் முகப் படத்தை எடுக்கவும்", capturingPhoto: "படம் எடுக்கிறது…",
      onlineCheck: "என்னைச் சரிபார்க்கவும்", onlineChecking: "சரிபார்க்கிறது…",
      onlineNotFound: "இந்த பணியாளர் எண்ணில் பதிவு எதுவும் இல்லை.",
      onlinePhotoMissing: "உங்கள் பணியாளர் பதிவில் சுயவிவரப் படம் இல்லை. தள நிர்வாகியின் உதவியைப் பெறவும்.",
      onlineFaceRead: "முகம் தெளிவாகத் தெரியும்படி வைத்து மீண்டும் முயற்சிக்கவும்.",
      onlineFaceMismatch: "உங்கள் முகம் இந்தப் பணியாளர் பதிவுடன் பொருந்தவில்லை. எண்ணைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.",
      onlineError: "இணையச் சரிபார்ப்பை முடிக்க முடியவில்லை. இணைப்பைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.",
      takePhoto: "படம் எடுக்கவும்", takeAgain: "மீண்டும் எடுக்கவும்", photoHint: "கேமராவைப் பார்த்து அசையாமல் இருங்கள் — படம் தானாக எடுக்கப்படும்",
      registerStart: "பதிவு செய்து ஸ்கேன் தொடங்கவும்", manualTitle: "உங்கள் விவரங்களை உள்ளிடவும்",
      manualHint: "விவரங்களை நிரப்பி ஸ்கேன் தொடங்கவும்.",
      fName: "முழுப் பெயர்", fAge: "வயது", fGender: "பாலினம்", fHeight: "உயரம் (செ.மீ.)", fWeight: "எடை (கி.கி.)",
      ageOrDob: "வயது அல்லது பிறந்த தேதி", dobEntry: "பிறந்த தேதி", ageEntry: "வயது",
      feet: "அடி", inches: "அங்குலம்", siteLabel: "தளம்", departmentLabel: "துறை",
      fixedSiteLoading: "தளம் ஏற்றப்படுகிறது…", deptLoading: "துறைகள் ஏற்றப்படுகின்றன…",
      deptError: "இந்தச் சாதனத்தின் தளம் மற்றும் துறைகளை ஏற்ற முடியவில்லை.", deptEmpty: "இந்தத் தளத்திற்கு துறைகள் அமைக்கப்படவில்லை.",
      retryDepartments: "மீண்டும் முயற்சிக்கவும்", deptSelect: "— துறையைத் தேர்ந்தெடுக்கவும் —",
      gMale: "ஆண்", gFemale: "பெண்", gOther: "மற்றவை",
      screeningTitle: "இந்த பணியாளருக்கான சோதனைகள்", togglePulmo: "நுரையீரல் சோதனை", toggleCognitive: "கவனச் சோதனை",
    },
    scan: {
      kicker: "முக ஸ்கேன் · பணியாளர் அடையாளம்", seconds: "வினாடிகள்",
      hr: "இதயத் துடிப்பு", hrv: "HRV", sdnn: "HRV · SDNN", rmssd: "HRV · RMSSD", pnn50: "HRV · pNN50",
      spo2: "இரத்த ஆக்சிஜன்", resp: "சுவாசம்", stress: "அழுத்தம்", bp: "இரத்த அழுத்தம்",
      opening: "கேமரா திறக்கப்படுகிறது…", openingSub: "சட்டகத்துக்குள் முகத்தை வைக்கவும்",
      prompt: "அசையாமல் இருங்கள் · கேமராவைப் பாருங்கள்", centerFace: "முகத்தை நடுவில் வைக்கவும்",
      noFace: "முகம் தெரியவில்லை — ஸ்கேன் நிறுத்தப்பட்டது", alignFace: "முகத்தைச் சீரமைக்கவும்",
      measuring: "அளவிடப்படுகிறது", done: "முடிந்தது", liveSignal: "நேரடி rPPG சிக்னல்",
      errTitle: "ஸ்கேன் முடிவடையவில்லை", embedTitle: "கேமராவுக்கு தனி தாவல் தேவை",
      embedMsg: "உட்பொதிக்கப்பட்ட முன்னோட்டத்தில் கேமரா இயங்காது. செயலியை தனி தாவலில் திறந்து அனுமதி வழங்கவும்.",
      openTab: "தனி தாவலில் திறக்கவும்",
      errCameraStart: "கேமரா ஸ்கேனைத் தொடங்க முடியவில்லை. அனுமதி மற்றும் இணையத்தைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.",
      errStopped: "கேமரா ஸ்கேன் எதிர்பாராமல் நின்றது. மீண்டும் முயற்சிக்கவும்.",
      errStartProper: "ஸ்கேன் சரியாகத் தொடங்கவில்லை. மீண்டும் முயற்சிக்கவும்.",
      errReadTime: "நேரத்திற்குள் உயிரளவுகளைப் படிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
      errReadVitals: "உயிரளவுகளைப் படிக்க முடியவில்லை. நல்ல வெளிச்சத்தில் அசையாமல் இருந்து மீண்டும் முயற்சிக்கவும்.",
      errFinishTime: "ஸ்கேன் நேரத்தில் முடிவடையவில்லை. மீண்டும் முயற்சிக்கவும்.",
      errLowFps: "ஸ்கேனின் போது கேமரா மெதுவாக இருந்தது. பிற செயலிகளை மூடி வெளிச்சத்தை அதிகரிக்கவும்.",
      errFroze: "ஸ்கேன் உறைந்தது. மீண்டும் முயற்சிக்கவும்.", errFeedStop: "கேமரா காட்சி நின்றது. மீண்டும் முயற்சிக்கவும்.",
      capturingHint: "அசையாமல் இருங்கள் — உயிரளவுகள் படிக்கப்படுகின்றன", chooseHint: "ஸ்கேன் முடிந்தது — தொடரவும்",
      doneHint: "ஸ்கேன் முடிந்தது — தொடரவும்", continue: "தொடரவும்",
    },
    pulmo: {
      kicker: "பால்மோஸ்கிரீன் · நுரையீரல் சோதனை", title: "நுரையீரல் சோதனை",
      coughOnlySub: "மைக்ரோஃபோனில் 2–3 முறை வலுவாக இருமவும். இருமல் மாதிரி மட்டும் சேகரிக்கப்படும்; பால்மோ மதிப்பெண் உருவாக்கப்படாது.",
      needTab: "மைக்ரோஃபோனுக்கு தனி தாவல் தேவை",
      needTabMsg: "உட்பொதிக்கப்பட்ட முன்னோட்டத்தில் மைக்ரோஃபோன் இயங்காது. செயலியை தனி தாவலில் திறந்து அனுமதி வழங்கவும்.",
      micDenied: "மைக்ரோஃபோனை அணுக முடியவில்லை. அனுமதி வழங்கி மீண்டும் முயற்சிக்கவும்.",
      step1Title: "இருமல் மாதிரி", step1Hint: "பதிவு தொடங்கியதும் மைக்ரோஃபோனில் 2–3 முறை வலுவாக இருமவும்.",
      startCough: "இருமலைப் பதிவு செய்யவும்", recording: "பதிவு செய்யப்படுகிறது…", uploading: "ஒலி பதிவேற்றப்படுகிறது…",
      complete: "நுரையீரல் சோதனை முடிந்தது",
      coughOnlyCompleteSub: "உங்கள் இருமல் மாதிரி சேகரிக்கப்பட்டது. இந்த இருமல் மட்டும் சோதனையில் பால்மோ மதிப்பெண் உருவாக்கப்படவில்லை.",
      errUpload: "பதிவை அனுப்ப முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
    },
    pvtb: {
      kicker: "கவனம் · PVT-B", title: "கவனச் சோதனை",
      intro: "வட்டம் நிரம்பியதும் முடிந்தவரை வேகமாகத் தட்டவும்—முன்கூட்டியே தட்டாதீர்கள். {n} சுற்றுகள்.",
      start: "தொடங்கவும்", wait: "காத்திருங்கள்…", tapNow: "தட்டவும்", tap: "இங்கே தட்டவும்",
      tooSoon: "மிக விரைவாகத் தட்டினீர்கள் — சிக்னலுக்குக் காத்திருங்கள்", round: "சுற்று {a} / {b}", ms: "மி.வி.",
      complete: "கவனச் சோதனை முடிந்தது", meanRt: "சராசரி எதிர்வினை", lapses: "தாமதங்கள்",
      resultSharp: "விழிப்பும் வேகமும் நன்று", resultOk: "இயல்பு வரம்பில்", resultSlow: "எதிர்வினை மெதுவாக உள்ளது — கவனமாக இருங்கள்",
    },
    result: {
      badge: "பணி தயார்நிலை", pulmoScore: "நுரையீரல் சோதனை", attention: "கவனம்",
      attentionMean: "சராசரி எதிர்வினை", attentionLapses: "தாமதங்கள்", uMs: "மி.வி.",
      scoresTitle: "உடல்நல மதிப்பெண்கள்", wellbeing: "நலநிலை", pulmonary: "நுரையீரல் நலம்", alertness: "விழிப்புணர்வு", na: "பொருந்தாது",
      reportTitle: "விரிவான அறிக்கை", colParam: "அளவீடு", colResult: "முடிவு", colRange: "குறிப்பு வரம்பு", colStatus: "நிலை",
      download: "அறிக்கையைப் பதிவிறக்கவும்", preparing: "அறிக்கை தயாராகிறது…",
      pFevFvc: "FEV1/FVC விகிதம்", pOad: "காற்றுப்பாதை அடைப்பு", oadTrue: "கண்டறியப்பட்டது", oadFalse: "கண்டறியப்படவில்லை", unavailable: "கிடைக்கவில்லை",
      coughSample: "இருமல் மாதிரி", collected: "சேகரிக்கப்பட்டது",
      clearLabel: "தகுதி", cautionLabel: "எச்சரிக்கை", referLabel: "பரிந்துரை",
      clearTitle: "இன்று பணிக்கு தகுதி", clearBody: "உங்கள் உயிரளவுகள் பாதுகாப்பான வரம்பில் உள்ளன. பணியைத் தொடங்கலாம்.",
      cautionTitle: "கவனத்துடன் பணிக்கு தகுதி", cautionBody: "ஒரு அளவீடு வரம்பைச் சற்றே மீறியுள்ளது. தண்ணீர் குடித்து மெதுவாகத் தொடங்கவும்.",
      referTitle: "சுகாதார அலுவலரைச் சந்திக்கவும்", referBody: "பாதுகாப்பு முக்கியமான பணிக்கு முன் உங்கள் உயிரளவுகளைச் சரிபார்க்க வேண்டும்.",
      logged: "டிஜிட்டல் உடல்நல பதிவில் சேர்க்கப்பட்டது", baselineUpdated: "தனிப்பட்ட அடிப்படை புதுப்பிக்கப்பட்டது",
      supervisor: "மேற்பார்வையாளருக்கு தெரிவிக்கப்பட்டது", ohc: "தொழில்சார் சுகாதார மையத்துக்கு பரிந்துரைக்கப்பட்டது",
      proceed: "பணிக்குச் செல்லவும்", restart: "அடுத்த பணியாளர்", disclaimer: "இது பணி தயார்நிலைக்கு உதவும் நலச் சோதனை; மருத்துவ நோயறிதல் அல்ல. உடல்நலம் சரியில்லை என்றால் சுகாதார மையத்தைத் தொடர்புகொள்ளவும்.",
    },
    siteJourney: {
      preparingKicker: "சாதனச் சோதனை", preparingTitle: "நிலையம் தயாராகிறது",
      preparingSub: "இந்தத் தளத்தின் பணியாளர் பட்டியல் ஏற்றப்படுகிறது. உங்கள் விவரங்கள் இந்தப் பகிரப்பட்ட சாதனத்திலேயே இருக்கும்.",
      preparingErrorTitle: "நிலையத்தைத் தயாரிக்க முடியவில்லை.", preparingErrorSub: "இணைப்பைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.",
      preparingRoster: "தளப் பணியாளர் பட்டியல் பாதுகாப்பாக ஏற்றப்படுகிறது",
      wellbeingKicker: "இன்னும் ஒரு கேள்வி", feelingQuestion: "உடல்நலம் சரியில்லையா?",
      feelingPrompt: "இப்போது உடல்நலம் நன்றாக உள்ளதா?",
      notOkay: "இல்லை", notOkayHint: "எனக்கு உதவி தேவை", imFine: "நான் நலமாக இருக்கிறேன்", connectTitle: "மெய்நிகர் OHC-ஐ தொடர்புகொள்ளவும்",
      connectSub: "தொழில்சார் சுகாதார மையக் குழுவிடமிருந்து அழைப்பைக் கோரவும். இப்போது கோரிக்கை மட்டும் பதிவு செய்யப்படும்; நேரடி அழைப்பு தொடங்காது.",
      callTeam: "மெய்நிகர் OHC அழைப்பைக் கோரவும்", loggingRequest: "கோரிக்கை பதிவு செய்யப்படுகிறது…",
      savingResult: "முடிவு சேமிக்கப்படுகிறது…", thankYou: "நன்றி. உங்கள் பதில் சேமிக்கப்படுகிறது…",
      requestLogged: "உங்கள் OHC கோரிக்கை பதிவானது", requestLoggedSub: "நிறுவன டாஷ்போர்டு வழியாக சுகாதாரக் குழு தொடர்புகொள்ள முடியும்.",
      resultSaved: "உங்கள் முடிவு சேமிக்கப்பட்டது", resultSavedSub: "இப்போது பணி வழிமுறைகளின்படி தொடரலாம்.",
      dashboardSynced: "முடிவு நிறுவன டாஷ்போர்டில் ஒத்திசைக்கப்பட்டது",
      retrySave: "மீண்டும் சேமிக்கவும்", nextWorker: "முடித்து அடுத்த பணியாளரைத் தொடங்கவும்",
    },
  },

  /* ───────────────────────── العربية (Arabic, RTL) ───────────────────────── */
  ar: {
    risk: { low: "منخفض", medium: "متوسط", moderate: "متوسط", high: "مرتفع" },
    common: { continue: "متابعة", back: "رجوع", tryAgain: "حاول مرة أخرى", poweredByShort: "مدعوم بواسطة" },
    getstarted: {
      kicker: "الجاهزية للعمل · فحص بدون تلامس",
      title1: "ابدأ ورديتك", title2: "بمسح الوجه.",
      sub: "اقترب من الكاميرا. خلال {n} ثانية تقريبًا نقرأ معدل ضربات قلبك والأكسجين والتنفس والمزيد — بدون أجهزة أو أساور.",
      p1: "مسح وجه بتقنية rPPG بدون تلامس",
      p2: "يُقاس مقابل نطاق آمن",
      p3: "نتيجة جاهزية واحدة واضحة",
      cta: "ابدأ",
      foot: "ليس تشخيصًا طبيًا",
    },
    identify: {
      kicker: "التعريف · التحقق من الموظف",
      matching: "جارٍ التعرّف عليك…",
      matchingSub: "مطابقة وجهك مع دليل الموظفين",
      verified: "تم التحقق",
      badgeLabel: "البطاقة",
      startScan: "ابدأ مسح الوجه",
      startHint: "تم التحقق — ابدأ مسح المؤشرات الحيوية بدون تلامس عندما تكون جاهزًا.",
      waitHint: "ابقَ ثابتًا — جارٍ التحقق من هويتك",
      starting: "جارٍ بدء المسح…",
      notYou: "لست أنت؟ أدخل التفاصيل يدويًا",
      notFoundKicker: "تعذّر التحقق من الموظف",
      notFound: "يرجى إدخال بياناتك يدويًا للمتابعة",
      enterManually: "أدخل التفاصيل يدويًا",
      firstTime: "أول مرة هنا؟ سجّل",
      registerTitle: "تسجيل عامل",
      manualWhy: "لا يوجد سجل لك بعد. هذه التفاصيل تنشئ سجل العامل الخاص بك وهي مطلوبة للفحص الصحي.",
      dob: "تاريخ الميلاد",
      computedAge: "العمر",
      contact: "رقم التواصل (اختياري)",
      alreadyExists: "معرّف الموظف هذا موجود بالفعل في هذا الموقع.",
      siteKicker: "اختيار الموقع",
      siteTitle: "اختر موقعك",
      siteSub: "سيقارن تسجيل الدخول بالوجه بينك وبين عمال هذا الموقع.",
      siteLoading: "جارٍ تحميل المواقع…",
      siteNone: "لا توجد مواقع متاحة بعد.",
      siteError: "تعذّر تحميل قائمة المواقع — تحقق من الاتصال وحاول مجددًا.",
      sitePreparing: "جارٍ تجهيز الموقع…",
      siteContinue: "المتابعة إلى تسجيل الوجه",
      takePhoto: "التقط صورة",
      takeAgain: "أعد التقاط الصورة",
      photoHint: "انظر إلى الكاميرا ثم التقط صورتك الشخصية",
      registerStart: "سجّل وابدأ المسح",
      manualTitle: "أدخل تفاصيلك",
      manualHint: "أدخل تفاصيلك ثم ابدأ المسح.",
      fName: "الاسم الكامل", fAge: "العمر", fGender: "الجنس", fHeight: "الطول (سم)", fWeight: "الوزن (كجم)",
      gMale: "ذكر", gFemale: "أنثى", gOther: "آخر",
      screeningTitle: "الفحوصات لهذا العامل",
      togglePulmo: "فحص الرئة (Pulmo)",
      toggleCognitive: "فحص الانتباه (PVT-B)",
    },
    scan: {
      kicker: "مسح الوجه · تعريف الموظف", seconds: "ثانية", bpm: "ن/د",
      hr: "معدل ضربات القلب", hrv: "تغيّر النبض", sdnn: "تغيّر النبض · SDNN", rmssd: "تغيّر النبض · RMSSD", pnn50: "تغيّر النبض · pNN50", spo2: "أكسجين الدم", resp: "التنفس", stress: "التوتر",
      bp: "ضغط الدم", bpNormal: "طبيعي", bpElevated: "مرتفع قليلًا", bpHigh: "مرتفع",
      lvlLow: "منخفض", lvlNormal: "طبيعي", lvlElevated: "مرتفع قليلًا", lvlHigh: "مرتفع",
      opening: "جارٍ فتح الكاميرا…", openingSub: "ضع وجهك داخل الإطار",
      prompt: "ابقَ ثابتًا · انظر إلى الكاميرا", centerFace: "ضع وجهك في المنتصف", noFace: "لم يُكتشف وجه — توقف المسح",
      alignFace: "حاذِ الوجه", measuring: "جارٍ القياس", done: "تم", liveSignal: "إشارة rPPG مباشرة",
      reading: "جارٍ قراءة مؤشراتك الحيوية…",
      errTitle: "لم يكتمل المسح", embedTitle: "تحتاج الكاميرا إلى علامة تبويب خاصة",
      embedMsg: "لا يمكن تشغيل الكاميرا داخل هذه المعاينة المضمّنة. افتح التطبيق في علامة تبويب خاصة، ثم اسمح بالوصول إلى الكاميرا عند الطلب.",
      openTab: "افتح في علامة تبويب خاصة",
      errCameraStart: "تعذّر بدء مسح الكاميرا. يرجى السماح بالوصول إلى الكاميرا، والتحقق من الاتصال، والمحاولة مرة أخرى.",
      errStopped: "توقف مسح الكاميرا بشكل غير متوقع. يرجى السماح بالوصول إلى الكاميرا، والتحقق من الاتصال، والمحاولة مرة أخرى.",
      errStartProper: "تعذّر بدء المسح بشكل صحيح. يرجى المحاولة مرة أخرى.",
      errReadTime: "تعذّر قراءة مؤشراتك الحيوية في الوقت المناسب. يرجى المحاولة مرة أخرى.",
      errReadVitals: "تعذّر قراءة مؤشراتك الحيوية. يرجى الجلوس بثبات في إضاءة جيدة والمحاولة مرة أخرى.",
      errFinishTime: "لم ينتهِ المسح في الوقت المناسب. يرجى المحاولة مرة أخرى.",
      errLowFps: "لم تتمكن الكاميرا من المواكبة أثناء المسح. أغلق التطبيقات الأخرى وحسّن الإضاءة وحاول مرة أخرى.",
      errFroze: "تجمّد المسح. يرجى المحاولة مرة أخرى.", errFeedStop: "توقفت تغذية الكاميرا. يرجى المحاولة مرة أخرى.",
      capturingHint: "ابقَ ثابتًا — تُقرأ مؤشراتك الحيوية",
      chooseHint: "اكتمل المسح — تابع",
      doneHint: "اكتمل المسح — تابع",
      continue: "متابعة",
    },
    pulmo: {
      kicker: "PulmoScreen · فحص الرئة", title: "فحص الرئة",
      sub: "سجّل سعلة قوية ثم مدّ صوت الحرف “آ” (/aː/). يحلّل CarePlix الصوت لإعطاء درجة فحص للرئة.",
      needTab: "يحتاج الميكروفون إلى علامة تبويب خاصة",
      needTabMsg: "لا يمكن تشغيل الميكروفون داخل هذه المعاينة المضمّنة. افتح التطبيق في علامة تبويب خاصة، ثم اسمح بالوصول إلى الميكروفون عند الطلب.",
      micDenied: "تعذّر الوصول إلى الميكروفون. يرجى السماح بالوصول إلى الميكروفون والمحاولة مرة أخرى.",
      step1Title: "الخطوة 1 · السعال", step1Hint: "عند بدء التسجيل، اسعل 2–3 سعلات قوية في الميكروفون.",
      step2Title: "الخطوة 2 · مدّ الحرف “آ”", step2Hint: "خذ نفسًا، ثم مدّ صوت الحرف “آ” (/aː/) ثابتًا حتى ينتهي المؤقّت.",
      startCough: "ابدأ تسجيل السعال", startVowel: "ابدأ تسجيل الحرف “آ”",
      recording: "جارٍ التسجيل…", recorded: "تم التسجيل", rerecord: "أعد التسجيل",
      uploading: "جارٍ رفع الصوت…",
      surveyTitle: "بضعة أسئلة سريعة", surveySub: "أجب بصدق — فهذا يحسّن دقة الفحص.",
      submit: "إرسال الإجابات", scoring: "جارٍ الحساب…",
      scoreLabel: "درجة فحص الرئة", complete: "اكتمل فحص الرئة",
      completeSub: "اكتمل تقييم الرئة. ستظهر نتائجك في تقرير الجاهزية النهائي.",
      errUpload: "تعذّر رفع التسجيل. يرجى المحاولة مرة أخرى.",
      errSurvey: "تعذّر تحميل الأسئلة.", errScore: "تعذّر حساب الدرجة. يرجى المحاولة مرة أخرى.",
      errScoreAudio: "تعذّر إنشاء الدرجة من هذه التسجيلات. لنُعد تسجيل السعال وصوت الحرف من جديد.",
    },
    pvtb: {
      kicker: "الانتباه · PVT-B", title: "فحص الانتباه",
      intro: "اختبار سريع لسرعة الاستجابة. عندما تمتلئ الدائرة، انقر بأسرع ما يمكن — لكن لا تنقر مبكرًا. {n} جولات.",
      start: "ابدأ", wait: "انتظر…", tapNow: "انقر", tap: "انقر هنا",
      tooSoon: "مبكر جدًا — انتظر الإشارة", round: "الجولة {a} من {b}", ms: "مي.ث",
      complete: "اكتمل فحص الانتباه", meanRt: "متوسط الاستجابة", lapses: "تأخّرات",
      resultSharp: "يقظ وسريع", resultOk: "ضمن النطاق", resultSlow: "استجابات بطيئة — كن حذرًا",
    },
    result: {
      badge: "الجاهزية", measured: "المؤشرات المقاسة",
      pulmoScore: "فحص الرئة", attention: "الانتباه (PVT-B)",
      attentionMean: "متوسط الاستجابة", attentionLapses: "تأخّرات",
      uBpm: "ن/د", uResp: "/د", uMs: "مي.ث",
      scoresTitle: "درجات الصحة", wellbeing: "العافية", pulmonary: "صحة الرئة", alertness: "اليقظة", na: "غير متاح",
      reportTitle: "تقرير مفصّل", colParam: "المؤشر", colResult: "النتيجة", colRange: "النطاق المرجعي", colStatus: "الحالة",
      download: "تنزيل التقرير", preparing: "جارٍ تجهيز التقرير…", reportHeading: "تقرير الجاهزية للعمل", worker: "العامل",
      bpExcellent: "ممتاز", bpGood: "جيد", bpAverage: "متوسط", bpFair: "مقبول", bpPoor: "ضعيف",
      pMap: "متوسط ضغط الشرايين", pCardiacOut: "النتاج القلبي",
      pHba1c: "HbA1c (تقديري)", pDiabRisk: "خطر السكري", pBmi: "مؤشر كتلة الجسم", pBmr: "معدل الأيض الأساسي",
      pBloodVolume: "حجم الدم", pBodyWater: "ماء الجسم", pBodyFat: "دهون الجسم", pVo2max: "VO₂ الأقصى",
      hba1cOptimal: "مثالي", hba1cAbove: "أعلى من المثالي",
      hypertensionRisk: "خطر ارتفاع ضغط الدم",
      cardiacRisk: "الخطر القلبي",
      diabRiskNoteOptimal: "خطر السكري: بما أن HbA1c لديك مثالي (< 5.7)، فإن هذه القيمة تشير إلى احتمال الإصابة بالسكري في المستقبل القريب.",
      diabRiskNoteAbove: "خطر السكري: بما أن HbA1c لديك أعلى من المثالي (≥ 5.7)، فإن هذه القيمة تشير إلى احتمال وجود قيمة أعلى من النطاق المثالي.",
      attentionNote: "متوسط الاستجابة يوضح مدى سرعة استجابتك للإشارة البصرية — كلما كان أسرع دلّ على يقظة أعلى. التأخّرات هي استجابات بطيئة بشكل غير معتاد (≥ 400 مي.ث) وتشير إلى انخفاض لحظي في الانتباه، وغالبًا ما ترتبط بالتعب أو النعاس.",
      pFevFvc: "نسبة FEV1/FVC", pOad: "انسداد المجرى الهوائي (OAD)", oadTrue: "مكتشف", oadFalse: "غير مكتشف", unavailable: "غير متاح",
      statusNormal: "طبيعي", statusWatch: "مراقبة", statusAction: "إجراء",
      clearLabel: "لائق", cautionLabel: "احتياط", referLabel: "إحالة",
      clearTitle: "لائق للعمل اليوم", clearBody: "مؤشراتك ضمن نطاق آمن. يمكنك البدء.",
      cautionTitle: "لائق مع الحذر", cautionBody: "قراءة واحدة خارج النطاق قليلًا دون أي علامات خطر. اشرب الماء وابدأ بهدوء — لائق للمتابعة مع المراقبة.",
      referTitle: "راجع مسؤول الصحة", referBody: "تحتاج مؤشراتك إلى فحص قبل العمل الحرِج للسلامة. تمت إحالتك إلى مركز الصحة المهنية — القرار للطبيب.",
      logged: "سُجّل في السجل الصحي الرقمي", baselineUpdated: "تم تحديث الأساس الشخصي",
      supervisor: "تم إبلاغ المشرف", ohc: "أُحيل إلى مركز الصحة المهنية",
      proceed: "اذهب إلى العمل", restart: "ابدأ عاملًا آخر",
      disclaimer: "هذا فحص عافية لدعم جاهزيتك للمناوبة — وليس تشخيصًا طبيًا. تُقاس المؤشرات الحيوية دون تلامس بواسطة CarePlix. إذا شعرت بتوعك، يُرجى التواصل مع المركز الصحي.",
    },
  },

  /* ───────────────────────── Français (French) ───────────────────────── */
  fr: {
    risk: { low: "Faible", medium: "Moyen", moderate: "Modéré", high: "Élevé" },
    common: { continue: "Continuer", back: "Retour", tryAgain: "Réessayer", poweredByShort: "Propulsé par" },
    getstarted: {
      kicker: "Aptitude au travail · Contrôle sans contact",
      title1: "Commencez votre poste", title2: "avec un scan facial.",
      sub: "Approchez-vous de la caméra. En {n} secondes environ, nous lisons votre fréquence cardiaque, l'oxygène, la respiration et plus — sans objet connecté, sans brassard.",
      p1: "Scan facial rPPG sans contact",
      p2: "Mesuré par rapport à une plage sûre",
      p3: "Un verdict d'aptitude clair",
      cta: "Commencer",
      foot: "Pas un diagnostic médical",
    },
    identify: {
      kicker: "Identification · Vérification employé",
      matching: "Identification en cours…",
      matchingSub: "Correspondance de votre visage avec l'annuaire des employés",
      verified: "Vérifié",
      badgeLabel: "Badge",
      startScan: "Démarrer le scan facial",
      startHint: "Vérifié — démarrez le scan des signes vitaux sans contact quand vous êtes prêt.",
      waitHint: "Restez immobile — vérification de votre identité",
      starting: "Démarrage du scan…",
      notYou: "Ce n'est pas vous ? Saisir les détails manuellement",
      notFoundKicker: "Employé non vérifié",
      notFound: "Veuillez saisir vos détails manuellement pour continuer",
      enterManually: "Saisir les détails manuellement",
      firstTime: "Première visite ? Inscrivez-vous",
      registerTitle: "Inscrire un travailleur",
      manualWhy: "Vous n'êtes pas encore enregistré. Ces informations créent votre dossier de travailleur et sont nécessaires pour le bilan de santé.",
      dob: "Date de naissance",
      computedAge: "Âge",
      contact: "Numéro de contact (facultatif)",
      alreadyExists: "Cet identifiant d'employé existe déjà sur ce site.",
      siteKicker: "Sélection du site",
      siteTitle: "Sélectionnez votre site",
      siteSub: "L'identification faciale vous comparera aux travailleurs de ce site.",
      siteLoading: "Chargement des sites…",
      siteNone: "Aucun site disponible pour le moment.",
      siteError: "Impossible de charger la liste des sites — vérifiez la connexion et réessayez.",
      sitePreparing: "Préparation du site…",
      siteContinue: "Continuer vers l'identification faciale",
      takePhoto: "Prendre la photo",
      takeAgain: "Reprendre la photo",
      photoHint: "Regardez la caméra, puis prenez votre photo de profil",
      registerStart: "S'inscrire et démarrer le scan",
      manualTitle: "Saisissez vos détails",
      manualHint: "Renseignez vos détails, puis démarrez le scan.",
      fName: "Nom complet", fAge: "Âge", fGender: "Sexe", fHeight: "Taille (cm)", fWeight: "Poids (kg)",
      gMale: "Homme", gFemale: "Femme", gOther: "Autre",
      screeningTitle: "Dépistages pour ce travailleur",
      togglePulmo: "Dépistage Pulmo (poumons)",
      toggleCognitive: "Test d'attention (PVT-B)",
    },
    scan: {
      kicker: "Scan facial · Identification employé", seconds: "Secondes", bpm: "BPM",
      hr: "Fréquence cardiaque", hrv: "VFC", sdnn: "VFC · SDNN", rmssd: "VFC · RMSSD", pnn50: "VFC · pNN50", spo2: "Oxygène sanguin", resp: "Respiration", stress: "Stress",
      bp: "Tension artérielle", bpNormal: "Normale", bpElevated: "Élevée", bpHigh: "Haute",
      lvlLow: "Faible", lvlNormal: "Normal", lvlElevated: "Élevé", lvlHigh: "Haut",
      opening: "Ouverture de la caméra…", openingSub: "Alignez votre visage dans le cadre",
      prompt: "Restez immobile · Regardez la caméra", centerFace: "Centrez votre visage", noFace: "Aucun visage détecté — scan en pause",
      alignFace: "Alignez le visage", measuring: "Mesure", done: "Terminé", liveSignal: "Signal rPPG en direct",
      reading: "Lecture de vos signes vitaux…",
      errTitle: "Scan non terminé", embedTitle: "La caméra nécessite son propre onglet",
      embedMsg: "La caméra ne peut pas fonctionner dans cet aperçu intégré. Ouvrez l'application dans son propre onglet de navigateur, puis autorisez l'accès à la caméra lorsque demandé.",
      openTab: "Ouvrir dans son propre onglet",
      errCameraStart: "Impossible de démarrer le scan. Autorisez l'accès à la caméra, vérifiez votre connexion et réessayez.",
      errStopped: "Le scan s'est arrêté de façon inattendue. Autorisez l'accès à la caméra, vérifiez votre connexion et réessayez.",
      errStartProper: "Le scan n'a pas pu démarrer correctement. Veuillez réessayer.",
      errReadTime: "Impossible de lire vos signes vitaux à temps. Veuillez réessayer.",
      errReadVitals: "Impossible de lire vos signes vitaux. Restez immobile sous une bonne lumière et réessayez.",
      errFinishTime: "Le scan ne s'est pas terminé à temps. Veuillez réessayer.",
      errLowFps: "La caméra n'a pas pu suivre pendant le scan. Fermez les autres applications, ajoutez de la lumière et réessayez.",
      errFroze: "Le scan s'est figé. Veuillez réessayer.", errFeedStop: "Le flux caméra s'est arrêté. Veuillez réessayer.",
      capturingHint: "Restez immobile — lecture de vos signes vitaux",
      chooseHint: "Scan terminé — continuez",
      doneHint: "Scan terminé — continuez",
      continue: "Continuer",
    },
    pulmo: {
      kicker: "PulmoScreen · Contrôle pulmonaire", title: "Dépistage pulmonaire",
      sub: "Enregistrez une toux ferme puis une voyelle « A » (/aː/) tenue. CarePlix analyse le son pour un score de dépistage pulmonaire.",
      needTab: "Le microphone nécessite son propre onglet",
      needTabMsg: "Le microphone ne peut pas fonctionner dans cet aperçu intégré. Ouvrez l'application dans son propre onglet, puis autorisez l'accès au microphone lorsque demandé.",
      micDenied: "Impossible d'accéder au microphone. Autorisez l'accès au micro et réessayez.",
      step1Title: "Étape 1 · Toux", step1Hint: "Quand l'enregistrement démarre, donnez 2 à 3 toux fermes dans le micro.",
      step2Title: "Étape 2 · Tenez la voyelle « A »", step2Hint: "Inspirez, puis tenez une voyelle « A » (/aː/) régulière jusqu'à la fin du minuteur.",
      startCough: "Démarrer l'enregistrement de la toux", startVowel: "Démarrer l'enregistrement de la voyelle « A »",
      recording: "Enregistrement…", recorded: "Enregistré", rerecord: "Réenregistrer",
      uploading: "Envoi de l'audio…",
      surveyTitle: "Quelques questions rapides", surveySub: "Répondez honnêtement — cela affine le dépistage.",
      submit: "Envoyer les réponses", scoring: "Calcul du score…",
      scoreLabel: "Score de dépistage Pulmo", complete: "Dépistage pulmonaire terminé",
      completeSub: "Évaluation Pulmo terminée. Vos résultats apparaîtront dans le rapport d'aptitude final.",
      errUpload: "Impossible d'envoyer l'enregistrement. Veuillez réessayer.",
      errSurvey: "Impossible de charger les questions.", errScore: "Impossible de calculer le score. Veuillez réessayer.",
      errScoreAudio: "Le score n'a pas pu être généré à partir de ces enregistrements. Réenregistrons la toux et la voyelle.",
    },
    pvtb: {
      kicker: "Attention · PVT-B", title: "Test d'attention",
      intro: "Un test de réaction rapide. Quand le cercle se remplit, tapez le plus vite possible — mais ne tapez pas trop tôt. {n} tours.",
      start: "Commencer", wait: "Attendez…", tapNow: "TAPEZ", tap: "Tapez ici",
      tooSoon: "Trop tôt — attendez le signal", round: "Tour {a} sur {b}", ms: "ms",
      complete: "Test d'attention terminé", meanRt: "Réaction moyenne", lapses: "Défaillances",
      resultSharp: "Vif et alerte", resultOk: "Dans la plage", resultSlow: "Réactions ralenties — prudence",
    },
    result: {
      badge: "Aptitude", measured: "Signes vitaux mesurés",
      pulmoScore: "Dépistage Pulmo", attention: "Attention (PVT-B)",
      attentionMean: "Réaction moy.", attentionLapses: "Défaillances",
      uBpm: "bpm", uResp: "/min", uMs: "ms",
      scoresTitle: "Scores de santé", wellbeing: "Bien-être", pulmonary: "Santé pulmonaire", alertness: "Vigilance", na: "N/D",
      reportTitle: "Rapport détaillé", colParam: "Paramètre", colResult: "Résultat", colRange: "Plage de référence", colStatus: "Statut",
      download: "Télécharger le rapport", preparing: "Préparation du rapport…", reportHeading: "Rapport d'aptitude au travail", worker: "Travailleur",
      bpExcellent: "Excellente", bpGood: "Bonne", bpAverage: "Moyenne", bpFair: "Passable", bpPoor: "Faible",
      pMap: "Pression artérielle moyenne", pCardiacOut: "Débit cardiaque",
      pHba1c: "HbA1c (est.)", pDiabRisk: "Risque de diabète", pBmi: "IMC", pBmr: "Métabolisme de base",
      pBloodVolume: "Volume sanguin", pBodyWater: "Eau corporelle", pBodyFat: "Masse grasse", pVo2max: "VO₂ max",
      hba1cOptimal: "Optimal", hba1cAbove: "Au-dessus de l'optimal",
      hypertensionRisk: "Risque d'hypertension",
      cardiacRisk: "Risque cardiaque",
      diabRiskNoteOptimal: "Risque de diabète : votre HbA1c étant optimale (< 5.7), cette valeur indique la probabilité de développer un diabète dans un avenir proche.",
      diabRiskNoteAbove: "Risque de diabète : votre HbA1c étant au-dessus de l'optimal (≥ 5.7), cette valeur indique la probabilité d'avoir une valeur au-dessus de la plage optimale.",
      attentionNote: "La réaction moyenne montre la rapidité de votre réponse à un signal visuel — plus elle est rapide, plus la vigilance est élevée. Les lapses sont des réponses anormalement lentes (≥ 400 ms), signalant des baisses momentanées d'attention, souvent liées à la fatigue ou à la somnolence.",
      pFevFvc: "Rapport FEV1/FVC", pOad: "Obstruction des voies aériennes (OAD)", oadTrue: "Détectée", oadFalse: "Non détectée", unavailable: "Indisponible",
      statusNormal: "Normal", statusWatch: "À surveiller", statusAction: "Action",
      clearLabel: "APTE", cautionLabel: "PRUDENCE", referLabel: "ORIENTER",
      clearTitle: "Apte au travail aujourd'hui", clearBody: "Vos signes vitaux sont dans une plage sûre. Vous pouvez commencer.",
      cautionTitle: "Apte avec réserve", cautionBody: "Une mesure est légèrement hors plage, sans signe d'alerte. Hydratez-vous et commencez doucement — apte à continuer sous surveillance.",
      referTitle: "Voir l'agent de santé", referBody: "Vos signes vitaux nécessitent un contrôle avant un travail critique pour la sécurité. Vous êtes orienté vers le Centre de Santé au Travail — le médecin décide.",
      logged: "Consigné au Registre de Santé numérique", baselineUpdated: "Référence personnelle mise à jour",
      supervisor: "Superviseur informé", ohc: "Orienté vers le Centre de Santé au Travail",
      proceed: "Aller au travail", restart: "Lancer un autre travailleur",
      disclaimer: "Ceci est un dépistage bien-être pour appuyer votre aptitude au poste — ce n'est pas un diagnostic médical. Les signes vitaux sont mesurés sans contact par CarePlix. En cas de malaise, contactez votre centre de santé.",
    },
  },
};

export function t(lang, key, vars) {
  const pick = (code) => {
    let node = STRINGS[code];
    for (const part of key.split(".")) {
      if (node == null) return undefined;
      node = node[part];
    }
    return node;
  };
  let val = pick(lang);
  if (val == null) val = pick("en");
  if (val == null) return key;
  if (typeof val === "string" && vars) {
    val = val.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
  }
  return val;
}
